import React, { useState, useEffect, useRef } from "react";
import {
  X,
  Clock,
  Loader2,
  AlertCircle,
  PlayCircle,
  FileText,
  Printer,
  XCircle,
  Search,
  Calendar,
} from "lucide-react";
import {
  getSalesOrders,
  getSalesOrderDetail,
  deleteSalesOrder,
} from "../../api/salesService";
import { masterService, salesTypeService } from "../../api/masterService";
import { configService } from "../../api/configService";
import { useNotification } from "../../contexts/NotificationContext";
import { executePrint } from "../../utils/printManager";
import { detectVehicleUnit } from "../../utils/receiptConfig";

const PAYMENT_STATUS_LABEL = {
  UNPAID: { label: "Belum Dibayar", cls: "text-gray-400" },
  PARTIAL: { label: "Bayar Sebagian", cls: "text-orange-500" },
  PAID: { label: "Lunas", cls: "text-green-600" },
};

const SELLER_LABEL = {
  INTERNAL: "Internal",
  EXTERNAL: "Umum",
  ONLINE: "Online",
};

// Cancel Confirmation Modal
const CancelConfirmModal = ({ order, onClose, onConfirm, isProcessing }) => {
  const [reason, setReason] = useState("");

  return (
    <div
      onClick={isProcessing ? undefined : onClose}
      className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center backdrop-blur-sm modal-backdrop-enter">
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl w-full max-w-md p-6 shadow-2xl modal-panel-enter">
        <div className="flex items-start gap-4 mb-4">
          <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
            <XCircle className="w-6 h-6 text-red-600" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-bold text-gray-900 mb-1">
              Batalkan Transaksi
            </h3>
            <p className="text-sm text-gray-600">
              Transaksi{" "}
              <span className="font-mono font-semibold">
                {order.transaction_number || order.id?.slice(0, 8)}
              </span>{" "}
              akan dibatalkan dan stock akan dikembalikan.
            </p>
          </div>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Alasan Pembatalan <span className="text-red-600">*</span>
          </label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Jelaskan alasan pembatalan transaksi..."
            maxLength={255}
            required
            aria-label="Alasan pembatalan transaksi"
            disabled={isProcessing}
            rows="4"
            className="w-full p-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 resize-none"
          />
        </div>

        <div className="flex gap-3">
          <button
            onClick={onClose}
            disabled={isProcessing}
            className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium text-sm disabled:opacity-50">
            Batal
          </button>
          <button
            onClick={() => reason.trim() && onConfirm(reason.trim())}
            disabled={isProcessing || !reason.trim()}
            className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed">
            {isProcessing ? "Memproses..." : "Batalkan Transaksi"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default function PendingTransactionsModal({
  isOpen,
  onClose,
  onResume,
  onOrderCancelled,
}) {
  const { showSuccess, showError } = useNotification();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [openMenu, setOpenMenu] = useState(null);
  const [cancelOrder, setCancelOrder] = useState(null);
  const [isCanceling, setIsCanceling] = useState(false);
  const [invoiceLoadingId, setInvoiceLoadingId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const filterBarRef = useRef(null);
  const searchFieldRef = useRef(null);
  const [recordInfoStart, setRecordInfoStart] = useState(290);
  const [filterDate, setFilterDate] = useState("");
  const [storeInfo, setStoreInfo] = useState({
    name: localStorage.getItem("tenantName") || "Toko",
    address: "",
    phone: "",
    logoUrl: "",
    footerNote: "",
  });
  const [salesTypes, setSalesTypes] = useState([]);

  const fetchOrders = () => {
    setLoading(true);
    setError(null);
    getSalesOrders({ status: "PENDING", limit: 50 })
      .then((res) => setOrders(res.data || []))
      .catch((err) => setError(err.message || "Gagal memuat transaksi pending"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!isOpen) return;
    fetchOrders();
    salesTypeService
      .list({ limit: 100 })
      .then((res) => setSalesTypes(res?.data || []))
      .catch(() => {});
  }, [isOpen]);

  const getOrderFinancialDetails = (order) => {
    const orderTypeId = String(order.order_type_id || "");
    const sellerTypeSlug = String(order.seller_type || "").toUpperCase();

    const matchedType = salesTypes.find((t) => {
      const tId = String(t.id || "");
      const tName = String(t.name || "").toUpperCase();
      const tSlug = String(t.slug || t.code || t.type || "").toUpperCase();
      return (
        (tId && orderTypeId && tId === orderTypeId) ||
        (tSlug && sellerTypeSlug && tSlug === sellerTypeSlug) ||
        (tName && sellerTypeSlug && tName === sellerTypeSlug)
      );
    });

    let discountVal = Number(order.discount_total || order.discount || 0);
    let markupVal = Number(order.additional_costs || order.additional_cost || 0);
    let grandTotal = Number(order.grand_total || order.total || 0);

    let itemsSubtotal = Number(order.subtotal || order.sub_total || 0);
    if (!itemsSubtotal && Array.isArray(order.items) && order.items.length > 0) {
      itemsSubtotal = order.items.reduce(
        (sum, item) => sum + Number(item.total_price ?? (Number(item.quantity || item.qty || 1) * Number(item.unit_price || item.price || 0))),
        0
      );
    }

    if (!itemsSubtotal) {
      itemsSubtotal = grandTotal + discountVal - markupVal;
    }

    if (discountVal === 0 && matchedType && matchedType.jenis === "Diskon") {
      const numNilai = Number(matchedType.nilai) || 0;
      discountVal = matchedType.satuan === "%" ? (itemsSubtotal * numNilai) / 100 : numNilai;
      grandTotal = Math.max(itemsSubtotal - discountVal, 0);
    }

    if (markupVal === 0 && matchedType && matchedType.jenis === "Markup") {
      const numNilai = Number(matchedType.nilai) || 0;
      markupVal = matchedType.satuan === "%" ? (itemsSubtotal * numNilai) / 100 : numNilai;
      grandTotal = itemsSubtotal + markupVal;
    }

    const discountPercent =
      discountVal > 0 && itemsSubtotal > 0
        ? Math.round((discountVal / itemsSubtotal) * 100)
        : matchedType && matchedType.jenis === "Diskon" && matchedType.satuan === "%"
        ? Number(matchedType.nilai) || 0
        : 0;

    const markupPercent =
      markupVal > 0 && itemsSubtotal > 0
        ? Math.round((markupVal / itemsSubtotal) * 100)
        : matchedType && matchedType.jenis === "Markup" && matchedType.satuan === "%"
        ? Number(matchedType.nilai) || 0
        : 0;

    return {
      matchedType,
      originalPrice: itemsSubtotal,
      grandTotal,
      discountVal,
      markupVal,
      discountPercent,
      markupPercent,
      hasDiscount: discountVal > 0,
      hasMarkup: markupVal > 0,
    };
  };

  useEffect(() => {
    if (!isOpen) return;
    let mounted = true;

    const loadStoreInfo = async () => {
      try {
        const res = await configService.getStore();
        const store = res?.data ?? res ?? {};
        if (!mounted) return;
        setStoreInfo({
          name: store?.name || localStorage.getItem("tenantName") || "Toko",
          address: store?.settings?.address || store?.address || "",
          phone: store?.settings?.phone || store?.phone || "",
          logoUrl: store?.logo_url || "",
          footerNote: store?.settings?.footer_note || "",
        });
      } catch {
        if (!mounted) return;
        setStoreInfo((prev) => ({
          ...prev,
          name: localStorage.getItem("tenantName") || prev.name || "Toko",
        }));
      }
    };

    loadStoreInfo();
    return () => {
      mounted = false;
    };
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const target = event.target;
      const clickedTrigger = target.closest("[data-menu-trigger]");
      const clickedMenu = target.closest("[data-pending-menu]");
      if (!clickedTrigger && !clickedMenu) {
        setOpenMenu(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const handleViewportChange = () => setOpenMenu(null);
    window.addEventListener("resize", handleViewportChange);
    window.addEventListener("scroll", handleViewportChange, true);
    return () => {
      window.removeEventListener("resize", handleViewportChange);
      window.removeEventListener("scroll", handleViewportChange, true);
    };
  }, []);

  const handleCancelTransaction = async (reason) => {
    if (!cancelOrder || isCanceling || !reason?.trim()) return;

    setIsCanceling(true);
    try {
      await deleteSalesOrder(cancelOrder.id, { cancellation_reason: reason.trim() });

      if (cancelOrder.table_id) {
        try {
          await masterService.tables.updateStatus(cancelOrder.table_id, {
            status: "AVAILABLE",
            reserved_by: "",
            notes: "",
          });
        } catch (tableErr) {
          console.warn("Gagal mereset status meja:", tableErr);
        }
      }

      showSuccess(
        `Transaksi ${cancelOrder.transaction_number || "pending"} berhasil dibatalkan`,
      );
      const cancelledItem = cancelOrder;
      setCancelOrder(null);
      fetchOrders(); // Refresh list
      if (typeof onOrderCancelled === "function") {
        onOrderCancelled(cancelledItem);
      }
    } catch (err) {
      showError(
        err.response?.data?.message ||
          err.message ||
          "Gagal membatalkan transaksi",
      );
    } finally {
      setIsCanceling(false);
    }
  };

  const handlePrintPendingReceipt = async (order, type = 'transaction') => {
    if (!order?.id) return;

    try {
      setInvoiceLoadingId(`${type}-${order.id}`);

      const detailRes = await getSalesOrderDetail(order.id);
      const d = detailRes?.data ?? detailRes ?? order;
      const items = (d?.items || []).map((item) => {
        const name =
          item.item_name ||
          item.product_name ||
          item.service_name ||
          item.name ||
          "-";
        const qty = Number(item.quantity || item.qty || 0);
        const price = Number(item.unit_price || item.price || 0);
        const total = Number(item.total_price ?? qty * price);
        const rawUnit = item.unit_name || item.unit?.name || (typeof item.unit === "string" ? item.unit : "");
        const physicalSatuan =
          item.satuan ||
          item.product_unit_name ||
          item.selling_unit_name ||
          item.selling_unit?.name ||
          (typeof item.selling_unit === "string" ? item.selling_unit : "") ||
          item.product?.unit ||
          item.product?.unit_name ||
          "";
        return {
          name,
          qty,
          price,
          total,
          discount_amount: Number(item.discount_amount || 0),
          additional_cost: Number(item.additional_cost || 0),
          unit: rawUnit || physicalSatuan || "",
          unit_name: rawUnit || "",
          asset_unit_name: item.asset_unit_name || "",
          satuan: physicalSatuan || "",
          notes: item.notes || item.note || "",
        };
      });

      const txDateTime = d?.created_at
        ? new Date(d.created_at).toLocaleString("id-ID", {
            day: "2-digit",
            month: "short",
            year: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
          })
        : "-";

      const grandTotal = Number(d?.grand_total ?? 0);
      const subtotalVal = Number(d?.subtotal ?? d?.sub_total ?? grandTotal);
      const payments = Array.isArray(d?.payments) ? d.payments : [];
      const tunaiAmount = Number(
        payments.length > 0
          ? payments.reduce((s, p) => s + Number(p?.amount || 0), 0)
          : (d?.amount_paid ?? 0),
      );

      const PAYMENT_METHOD_NAME = {
        CASH: "Tunai",
        TRANSFER: "Transfer",
        QRIS: "QRIS",
        MIXED: "Campuran",
      };
      const uniqueMethods = Array.from(
        new Set(
          payments
            .map((p) => (p?.method || p?.payment_method || "").toUpperCase())
            .filter(Boolean),
        ),
      );
      const paymentMethodLabel =
        uniqueMethods.length > 1
          ? "Campuran"
          : uniqueMethods.length === 1
            ? PAYMENT_METHOD_NAME[uniqueMethods[0]] || uniqueMethods[0]
            : "Tunai";

      const receiptData = {
        storeName: storeInfo?.name || localStorage.getItem("tenantName") || "Toko",
        storeAddress: storeInfo?.address || "",
        storePhone: storeInfo?.phone || "",
        storeLogoUrl: storeInfo?.logoUrl || localStorage.getItem("tenantLogoUrl") || "",
        storeFooterNote: storeInfo?.footerNote || "Terima Kasih Telah Menjadi Pelanggan Kami",
        meta: {
          transactionNumber: d?.transaction_number || "-",
          transactionDateTime: txDateTime,
          queueNumber: d?.queue_number || d?.no_urut,
          cashierName: d?.created_by || d?.cashier_name || "-",
          customerName: d?.customer_name || "Pelanggan Umum",
          customerPhone:
            d?.customer_phone ||
            d?.customer?.phone ||
            d?.customer?.phone_number ||
            "",
          customerAddress: d?.customer_address || d?.customer?.address || "",
          unitName: d?.unit_name || d?.asset_unit_name || detectVehicleUnit(d, d?.items) || "",
          sellerType: SELLER_LABEL[d?.seller_type] ?? d?.seller_type ?? "-",
          transactionNotes: d?.notes || d?.note || "",
          status:
            type === "order"
              ? "NOTA PESANAN (DAPUR)"
              : PAYMENT_STATUS_LABEL[d?.payment_status]?.label ||
                d?.payment_status ||
                "-",
        },
        items,
        totals: {
          subtotal: subtotalVal,
          discount: Number(d?.discount_total ?? d?.discount ?? 0),
          additionalCosts: Number(d?.additional_costs ?? d?.additional_cost ?? 0),
          total: grandTotal,
          tunai: tunaiAmount,
          totalBayar: grandTotal,
          paymentMethodLabel,
          change: Math.max(tunaiAmount - grandTotal, 0),
          isDownPayment: tunaiAmount > 0 && tunaiAmount < grandTotal,
          remaining: Math.max(grandTotal - tunaiAmount, 0),
        },
        print: {
          paidAt: txDateTime,
          printedBy: d?.created_by || d?.cashier_name || "-",
        },
      };

      await executePrint(receiptData, type);
    } catch (err) {
      showError(
        err?.response?.data?.message || err?.message || "Gagal mencetak nota",
      );
    } finally {
      setInvoiceLoadingId(null);
    }
  };

  if (!isOpen) return null;

  const isApps = localStorage.getItem("PREVIEW_APPS") === "true";

  const filteredOrders = orders.filter((order) => {
    const matchSearch =
      !searchQuery ||
      (order.transaction_number || "")
        .toLowerCase()
        .includes(searchQuery.toLowerCase()) ||
      (order.customer_name || "")
        .toLowerCase()
        .includes(searchQuery.toLowerCase());

    const matchDate =
      !filterDate ||
      (order.created_at && order.created_at.startsWith(filterDate));

    return matchSearch && matchDate;
  });

  const totalAmount = filteredOrders.reduce(
    (sum, o) => sum + Number(o.grand_total || 0),
    0,
  );

  useEffect(() => {
    const alignRecordDivider = () => {
      if (!filterBarRef.current || !searchFieldRef.current) return;
      const barRect = filterBarRef.current.getBoundingClientRect();
      const searchRect = searchFieldRef.current.getBoundingClientRect();
      setRecordInfoStart(Math.max(220, Math.round(searchRect.left - barRect.left)));
    };

    alignRecordDivider();
    const observer = new ResizeObserver(alignRecordDivider);
    if (filterBarRef.current) observer.observe(filterBarRef.current);
    if (searchFieldRef.current) observer.observe(searchFieldRef.current);
    window.addEventListener("resize", alignRecordDivider);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", alignRecordDivider);
    };
  }, [totalAmount, filteredOrders.length]);

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center backdrop-blur-sm modal-backdrop-enter">
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl w-full max-w-4xl shadow-2xl max-h-[90vh] flex flex-col modal-panel-enter">
        {/* Header */}
        <div className="flex justify-between items-start px-6 pt-6 pb-4 border-b">
          <div>
            <h2 className="text-xl font-bold text-gray-800">
              Transaksi Pending
            </h2>
            <p className="text-sm text-gray-400 mt-0.5">
              Informasi mengenai transaksi tersimpan atau belum dibayar
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Stats + Filter Bar */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 px-6 py-4 border-b">
          {/* Total amount & Count container (228px + 12px gap = 240px to search input) */}
          <div className="sm:w-[228px] shrink-0 flex items-center gap-2">
            <div className="flex-1 min-w-0 flex items-center justify-center px-2.5 py-2 border border-gray-200 rounded-lg bg-slate-50/50">
              <span className="text-sm font-bold text-red-500 truncate">
                Rp. {totalAmount.toLocaleString("id-ID")}
              </span>
            </div>
            <div className="flex items-center justify-center gap-1 px-2.5 py-2 border border-gray-200 rounded-lg bg-slate-50/50 shrink-0">
              <span className="text-sm font-bold text-orange-500">
                {filteredOrders.length}
              </span>
              <span className="text-xs text-gray-500 font-medium">Transaksi</span>
            </div>
          </div>

          {/* Search + Date Filter (starts at exactly 240px from inner padding) */}
          <div className="flex-1 flex items-center gap-3 min-w-0">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Cari transaksi..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-400 focus:border-transparent"
              />
            </div>

            <div className="relative shrink-0">
              <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              <input
                type="date"
                value={filterDate}
                onChange={(e) => setFilterDate(e.target.value)}
                className="pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-500 focus:ring-2 focus:ring-teal-400 focus:border-transparent w-36 sm:w-40"
              />
            </div>
          </div>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-2">
          {loading && (
            <div className="flex items-center justify-center py-12 gap-3">
              <Loader2 className="w-6 h-6 animate-spin text-teal-600" />
              <span className="text-gray-500">Memuat transaksi pending...</span>
            </div>
          )}

          {!loading && error && (
            <div className="flex flex-col items-center justify-center py-12 gap-3 text-red-500">
              <AlertCircle className="w-6 h-6" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          {!loading && !error && filteredOrders.length === 0 && (
            <div className="text-center py-12 text-gray-400">
              <p className="font-medium">Tidak ada transaksi pending</p>
              <p className="text-sm mt-1">
                Semua transaksi sudah selesai atau dibatalkan
              </p>
            </div>
          )}

          {!loading &&
            !error &&
            filteredOrders.map((order) => {
              const createdAt = order.created_at
                ? new Date(order.created_at)
                : null;
              const createdLabel = createdAt
                ? createdAt.toLocaleDateString("id-ID", {
                    weekday: "long",
                    day: "2-digit",
                    month: "long",
                    year: "numeric",
                  }) +
                  " | " +
                  createdAt.toLocaleTimeString("id-ID", {
                    hour: "2-digit",
                    minute: "2-digit",
                  }) +
                  " WIB"
                : "-";
              const isMenuOpen = openMenu?.id === order.id;
              const sellerLabel =
                SELLER_LABEL[order.seller_type] ?? order.seller_type ?? "-";

              const fin = getOrderFinancialDetails(order);
              const remainingVal = order.total_paid > 0 ? Math.max(fin.grandTotal - order.total_paid, 0) : fin.grandTotal;

              return (
                <div
                  key={order.id}
                  data-menu-trigger="true"
                  onClick={(e) => {
                    if (isMenuOpen) {
                      setOpenMenu(null);
                      return;
                    }
                    const rect = e.currentTarget.getBoundingClientRect();
                    const menuWidth = 192;
                    const menuHeight = 148;
                    const padding = 12;
                    let left = rect.right - menuWidth;
                    left = Math.max(
                      padding,
                      Math.min(
                        left,
                        window.innerWidth - menuWidth - padding,
                      ),
                    );
                    let top = rect.bottom + 8;
                    if (top + menuHeight > window.innerHeight - padding)
                      top = Math.max(padding, rect.top - menuHeight - 8);
                    setOpenMenu({ id: order.id, top, left, order });
                  }}
                  className={`border rounded-xl bg-white hover:shadow-sm transition-all flex flex-col sm:flex-row items-stretch overflow-hidden cursor-pointer ${
                    isMenuOpen
                      ? "border-teal-400 ring-2 ring-teal-100"
                      : "border-gray-200 hover:border-gray-300"
                  }`}>

                  {/* Transaction number + date (Column 1 compact width 240px = 228px + 12px gap, 100% aligned with search bar) */}
                  <div className="sm:w-[240px] shrink-0 px-4 py-3.5 min-w-0 sm:border-r border-gray-200">
                    <p className="font-bold text-gray-900 text-sm">
                      {order.transaction_number || order.id?.slice(0, 8)}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">{createdLabel}</p>
                  </div>

                  {/* Info kolom: Pelanggan / Tipe Penjualan / Total Tagihan */}
                  <div className="flex-1 grid grid-cols-3 divide-x divide-gray-200 border-t sm:border-t-0">
                    {/* Pelanggan */}
                    <div className="px-3 sm:px-5 py-4 flex flex-col justify-center min-w-0">
                      <p className="text-xs text-gray-400 mb-0.5">Pelanggan</p>
                      <p className="font-bold text-gray-900 text-sm truncate">
                        {order.customer_name || "Pelanggan Umum"}
                      </p>
                    </div>

                    {/* Tipe Penjualan */}
                    <div className="px-3 sm:px-5 py-4 flex flex-col justify-center min-w-0">
                      <p className="text-xs text-gray-400 mb-0.5">
                        Tipe Penjualan
                      </p>
                      <p className="font-bold text-gray-900 text-sm truncate">
                        {sellerLabel}
                      </p>
                    </div>

                    {/* Total Tagihan / Sisa Tagihan */}
                    <div className="px-3 sm:px-5 py-4 flex flex-col justify-center text-right min-w-0">
                      <p className="text-xs text-gray-400 mb-0.5">
                        {order.total_paid > 0 ? "Sisa Tagihan" : "Total Tagihan"}
                      </p>
                      <p className="font-bold text-red-500 text-sm truncate">
                        Rp. {remainingVal.toLocaleString("id-ID")}
                      </p>
                      {order.total_paid > 0 && (
                        <p className="text-[10px] text-amber-600 font-medium mt-0.5 truncate">
                          (DP: Rp {(order.total_paid || 0).toLocaleString("id-ID")})
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
        </div>
      </div>

      {openMenu && (
        <div
          data-pending-menu="true"
          onClick={(e) => e.stopPropagation()}
          className="fixed w-52 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-[70]"
          style={{ top: openMenu.top, left: openMenu.left }}>
          <button
            onClick={() => {
              const o = openMenu.order;
              setOpenMenu(null);
              onResume(o);
            }}
            className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-teal-50 text-left text-sm transition-colors">
            <PlayCircle className="w-4 h-4 text-teal-600" />
            <span className="text-gray-700 font-medium">Lanjutkan</span>
          </button>

          <button
            onClick={() => {
              const o = openMenu.order;
              setOpenMenu(null);
              handlePrintPendingReceipt(o, "transaction");
            }}
            disabled={invoiceLoadingId === `transaction-${openMenu.id}`}
            className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-blue-50 text-left text-sm transition-colors">
            {invoiceLoadingId === `transaction-${openMenu.id}` ? (
              <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />
            ) : (
              <FileText className="w-4 h-4 text-blue-600" />
            )}
            <span className="text-gray-700 font-medium">Cetak Nota Bayar</span>
          </button>

          {isApps && (
            <button
              onClick={() => {
                const o = openMenu.order;
                setOpenMenu(null);
                handlePrintPendingReceipt(o, "order");
              }}
              disabled={invoiceLoadingId === `order-${openMenu.id}`}
              className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-amber-50 text-left text-sm transition-colors">
              {invoiceLoadingId === `order-${openMenu.id}` ? (
                <Loader2 className="w-4 h-4 text-amber-600 animate-spin" />
              ) : (
                <Printer className="w-4 h-4 text-amber-600" />
              )}
              <span className="text-gray-700 font-medium">Cetak Nota Pesanan</span>
            </button>
          )}

          <hr className="my-1 border-gray-200" />

          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              const o = openMenu.order;
              setOpenMenu(null);
              setCancelOrder(o);
            }}
            className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-red-50 text-left text-sm transition-colors">
            <XCircle className="w-4 h-4 text-red-600" />
            <span className="text-red-600 font-medium">Batalkan</span>
          </button>
        </div>
      )}

      {cancelOrder && (
        <CancelConfirmModal
          order={cancelOrder}
          onClose={() => setCancelOrder(null)}
          onConfirm={handleCancelTransaction}
          isProcessing={isCanceling}
        />
      )}
    </div>
  );
}
